/**
 * The workday behind the 0.
 *
 * Prest0's brand mark is a zero, and the product is priced in units of
 * finished work. So the hero's zero is a 24-hour ring: each stop is one real
 * thing a Prest0n does at that hour, and the cost beside it is the PUBLISHED
 * rate for that kind of work, imported from src/config/workUnits.ts rather
 * than typed here. Drag the ring and the month's total accumulates toward
 * exactly the allocation one hire includes — so the hero and the price are the
 * same object, and every number on screen is one the buyer can check.
 *
 * INDUSTRY IS AN EXPLICIT CHOICE, NEVER AN INFERENCE. The visitor picks "law
 * firm" or "finance firm" from a chip; nothing here reads their typed text or
 * guesses. That keeps the page a renderer of pre-authored content and leaves
 * every actual decision with the primary orchestrator, per the master rule.
 *
 * The labels are the only authored strings. Each is a real capability the
 * platform has today: Routines that trigger on an event, cited research
 * delivered as PDF and Word, batch document intake returned as one
 * deliverable, EN↔ES translation, voice, and the nightly memory consolidation.
 * Nothing here describes something unshipped.
 */

import { WORK_RATE_CARD } from '../../config/workUnits';

export type Vertical = 'law' | 'finance' | 'other';

/** Rate-card row ids, so a label can cite its own price without retyping it. */
type RateKey =
  | 'deep-research memo'
  | 'drafted document'
  | 'certified-style translation'
  | 'document intake & analysis'
  | 'task turn · Routine run · voice minute'
  | 'overruns · retries · failovers · thinking depth';

/** Looks the published rate up by task name. Throws loudly if the card moves. */
export function rateFor(task: RateKey): number {
  const row = WORK_RATE_CARD.find((r) => r.task === task);
  if (!row) {
    throw new Error(
      `zeroDay: no rate-card row named "${task}". The card is the source of truth — ` +
        'update these keys rather than hard-coding a number.',
    );
  }
  return row.wu;
}

export interface DayStop {
  /** 0-23. */
  hour: number;
  /** What it is doing, in the house voice. */
  label: string;
  /** The published cost of that work, or 0 for the free row. */
  wu: number;
  /** Shown under the label — the unit the rate is charged in. */
  unit: string;
}

const t = (task: RateKey) => ({ wu: rateFor(task), unit: WORK_RATE_CARD.find((r) => r.task === task)!.unit });

/** Shared by every vertical: the hours that are about the platform, not the trade. */
function commonStops(): DayStop[] {
  return [
    { hour: 23, label: 'consolidates what it learned today', ...t('overruns · retries · failovers · thinking depth') },
  ];
}

const LAW: DayStop[] = [
  { hour: 2, label: 'watches the docket and files what arrives', ...t('task turn · Routine run · voice minute') },
  { hour: 7, label: 'the morning brief, cited, as PDF and Word', ...t('deep-research memo') },
  { hour: 10, label: 'drafts the motion in your voice', ...t('drafted document') },
  { hour: 13, label: 'reads the new client file — dates, obligations, risks', ...t('document intake & analysis') },
  { hour: 16, label: 'calls the lead back, in English or Spanish', ...t('task turn · Routine run · voice minute') },
  { hour: 19, label: 'translates the exhibit, certified-style', ...t('certified-style translation') },
  ...commonStops(),
];

const FINANCE: DayStop[] = [
  { hour: 2, label: 'watches the filings and files what arrives', ...t('task turn · Routine run · voice minute') },
  { hour: 7, label: 'the morning brief, cited, as PDF and Word', ...t('deep-research memo') },
  { hour: 10, label: 'drafts the client letter in your voice', ...t('drafted document') },
  { hour: 13, label: 'reads the statements — figures, dates, exceptions', ...t('document intake & analysis') },
  { hour: 16, label: 'calls the client back, in English or Spanish', ...t('task turn · Routine run · voice minute') },
  { hour: 19, label: 'translates the disclosure, certified-style', ...t('certified-style translation') },
  ...commonStops(),
];

const OTHER: DayStop[] = [
  { hour: 2, label: 'watches what you told it to watch, and files it', ...t('task turn · Routine run · voice minute') },
  { hour: 7, label: 'the morning brief, cited, as PDF and Word', ...t('deep-research memo') },
  { hour: 10, label: 'drafts the document in your voice', ...t('drafted document') },
  { hour: 13, label: 'reads the files — dates, obligations, risks', ...t('document intake & analysis') },
  { hour: 16, label: 'calls the lead back, in English or Spanish', ...t('task turn · Routine run · voice minute') },
  { hour: 19, label: 'translates it, certified-style', ...t('certified-style translation') },
  ...commonStops(),
];

export function dayFor(vertical: Vertical): DayStop[] {
  const stops = vertical === 'law' ? LAW : vertical === 'finance' ? FINANCE : OTHER;
  return [...stops].sort((a, b) => a.hour - b.hour);
}

/** What the visible stops cost in one pass round the clock. */
export function dayTotalWu(vertical: Vertical): number {
  return dayFor(vertical).reduce((sum, s) => sum + s.wu, 0);
}

/** Twelve o'clock is straight up; hours run clockwise. */
export function hourToAngle(hour: number): number {
  return (hour / 24) * 360 - 90;
}
